import { memo, useCallback } from 'react'
import { AlertCircle } from 'lucide-react'
import { VirtualMessageList } from './VirtualMessageList'
import type { Message } from '../../../types/chat.types'
import type { Agent } from '../../../services/agentService'
import { ChatWelcomeScreen } from './ChatWelcomeScreen'
import { useChatStore } from '../../../stores/chatStore'

interface ChatMessagesProps {
    agentId: string | null
    messages: Message[]
    isLoading?: boolean
    agentName: string
    agentAvatar?: string | null
    agent?: Agent | null
    onCopy?: (content: string, index: number) => void
    onFeedback?: (index: number, feedback: 'liked' | 'disliked') => void
    copiedMessageId?: number | null
    hasAttemptedLoad?: boolean
    loadError?: Error | null
}

/**
 * Production-Grade Chat Messages Component
 */
export const ChatMessages: React.FC<ChatMessagesProps> = memo(({
    agentId,
    messages,
    isLoading = false,
    agentName,
    agentAvatar,
    agent,
    onCopy,
    onFeedback,
    copiedMessageId,
    hasAttemptedLoad = false,
    loadError = null
}) => {
    // Phase 4.1: Atomic High-Frequency Selector
    // This isolates token updates (streaming) from the rest of the message list.
    const streamingMessage = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.streamingMessage || null : null
        , [agentId]))

    if (loadError) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                    <div className="text-red-500 mb-4">
                        <AlertCircle className="w-12 h-12 mx-auto" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Failed to Load Messages
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {loadError.message || 'An unexpected error occurred'}
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Reload Page
                    </button>
                </div>
            </div>
        )
    }

    if (messages.length === 0 && (isLoading || !hasAttemptedLoad)) {
        return <div className="flex-1" />
    }

    if (messages.length === 0 && !streamingMessage && hasAttemptedLoad && agent) {
        return <ChatWelcomeScreen agent={agent} />
    }

    return (
        <div className="flex-1 overflow-hidden h-full relative">
            <VirtualMessageList
                messages={messages}
                streamingMessage={streamingMessage}
                agentName={agentName}
                agentAvatar={agentAvatar}
                agent={agent}
                onCopy={onCopy}
                onFeedback={onFeedback}
                copiedMessageId={copiedMessageId}
            />
        </div>
    )
})
